import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatPrice, type TokenBundle, type SubscriptionOption } from '@/lib/bundles';
import { PRODUCT_NAME, STUDIO_NAME, STUDIO_URL, STATEMENT_DESCRIPTOR_FULL } from '@/lib/brand';
import {
  createCheckoutSession,
  createSubscriptionCheckoutSession,
  createBillingPortalSession,
  fetchBillingOptions,
  fetchSubscriptionStatus,
  fetchTokenTransactions,
  type TokenTransaction,
} from '@/lib/checkout';

export default function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bundles, setBundles] = useState<TokenBundle[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionOption | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subscriptionStatusLoading, setSubscriptionStatusLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const success = searchParams.get('success') === '1';
  const canceled = searchParams.get('canceled') === '1';

  // Notify the extension to refresh token balance, then clear URL params.
  // Small delay so the extension content script has time to attach its listener.
  useEffect(() => {
    if (!success && !canceled) return;
    if (success) {
      const t = setTimeout(() => {
        try {
          document.dispatchEvent(new CustomEvent('shopmi-tokens-purchased'));
        } catch {
          // ignore
        }
        setSearchParams({}, { replace: true });
      }, 300);
      return () => clearTimeout(t);
    }
    setSearchParams({}, { replace: true });
  }, [success, canceled, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchBillingOptions()
      .then(({ bundles: b, subscription: s }) => {
        if (!cancelled) {
          setBundles(b);
          setSubscription(s);
        }
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load options');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSubscriptionStatusLoading(true);
    fetchSubscriptionStatus()
      .then((active) => {
        if (!cancelled) setHasActiveSubscription(active);
      })
      .catch(() => {
        if (!cancelled) setHasActiveSubscription(false);
      })
      .finally(() => {
        if (!cancelled) setSubscriptionStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTransactionsLoading(true);
    setTransactionsError(null);
    fetchTokenTransactions(50)
      .then((list) => {
        if (!cancelled) setTransactions(list);
      })
      .catch((e) => {
        if (!cancelled) setTransactionsError(e instanceof Error ? e.message : 'Failed to load history');
      })
      .finally(() => {
        if (!cancelled) setTransactionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleBuy(bundleId: string) {
    setError(null);
    setLoadingId(bundleId);
    try {
      const url = await createCheckoutSession(bundleId);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout');
      setLoadingId(null);
    }
  }

  async function handleSubscribe() {
    setError(null);
    setLoadingId('subscription');
    try {
      const url = await createSubscriptionCheckoutSession();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout');
      setLoadingId(null);
    }
  }

  async function handleManageSubscription() {
    setError(null);
    setLoadingId('manage');
    try {
      const url = await createBillingPortalSession();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open billing portal');
      setLoadingId(null);
    }
  }


  return (
    <div className="max-w-2xl">
      <h1 className="font-display mb-2 text-2xl font-semibold tracking-wide text-mirror-text">Top up credits</h1>
      <p className="mb-2 text-mirror-muted">
        Buy token packs or subscribe for a monthly allowance. Each generation uses 20 tokens.
      </p>
      <p className="mb-6 text-sm text-mirror-muted">
        {PRODUCT_NAME} is a product of{' '}
        <a href={STUDIO_URL} target="_blank" rel="noopener noreferrer" className="text-mirror-gold hover:underline">
          {STUDIO_NAME}
        </a>
        . Charges appear on your statement as <strong>{STATEMENT_DESCRIPTOR_FULL}</strong>.
      </p>

      {success && (
        <div className="mb-6 rounded-xl border border-mirror-border bg-mirror-card p-4 text-mirror-text border-l-4 border-l-emerald-500/80">
          Payment successful. Your tokens have been added and will appear in the extension shortly.
        </div>
      )}
      {canceled && (
        <div className="mb-6 rounded-xl border border-mirror-border bg-mirror-card p-4 text-mirror-muted border-l-4 border-l-amber-500/80">
          Checkout was canceled. You can pick an option below when you’re ready.
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-xl border border-mirror-border bg-mirror-card p-4 text-red-300 border-l-4 border-l-red-500/80">
          {error}
        </div>
      )}

      {loading && (
        <div className="rounded-xl border border-mirror-border bg-mirror-card p-8 text-center text-mirror-muted">
          Loading options…
        </div>
      )}
      {loadError && (
        <div className="rounded-xl border border-mirror-border bg-mirror-card p-4 text-red-300 border-l-4 border-l-red-500/80">
          {loadError}
        </div>
      )}

      {!loading && !loadError && (
        <>
          {subscription && (
            <section className="mb-8">
              <h2 className="mb-3 text-lg font-medium text-mirror-text">Subscription</h2>
              <div className="rounded-xl border border-mirror-border bg-mirror-card p-5 transition-[border-color] hover:border-[rgba(201,169,98,0.3)]">
                <div className="mb-1 font-semibold text-mirror-text">{subscription.label}</div>
                <div className="mb-3 text-2xl font-bold text-mirror-gold">
                  {formatPrice(subscription.amountCents)}
                  <span className="ml-1 text-base font-normal text-mirror-muted">/ month</span>
                </div>
                {subscriptionStatusLoading ? (
                  <p className="text-sm text-mirror-muted">Checking status…</p>
                ) : hasActiveSubscription ? (
                  <button
                    type="button"
                    className="rounded-lg border border-mirror-border bg-mirror-surface px-4 py-2.5 text-sm font-medium text-mirror-text hover:bg-mirror-border transition-colors disabled:opacity-60"
                    onClick={handleManageSubscription}
                    disabled={!!loadingId}
                  >
                    {loadingId === 'manage' ? 'Redirecting…' : 'Manage subscription'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="rounded-lg bg-mirror-gold px-4 py-2.5 text-sm font-semibold text-mirror-bg hover:bg-mirror-gold-hover transition-colors disabled:opacity-60"
                    onClick={handleSubscribe}
                    disabled={!!loadingId}
                  >
                    {loadingId === 'subscription' ? 'Redirecting…' : 'Subscribe'}
                  </button>
                )}
              </div>
            </section>
          )}

          <section>
            <h2 className="mb-3 text-lg font-medium text-mirror-text">One-time packs</h2>
            {bundles.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {bundles.map((bundle) => {
                  const price = formatPrice(bundle.amountCents);
                  const byLine = bundle.detailLine;
                  return (
                    <div
                      key={bundle.id}
                      className="flex flex-col rounded-xl border border-mirror-border bg-mirror-card p-5 transition-[border-color] hover:border-[rgba(201,169,98,0.3)]"
                    >
                      <div className="mb-1 font-semibold text-mirror-text">{bundle.label}</div>
                      <div className="mb-1 text-2xl font-bold text-mirror-gold">{price}</div>
                      <div className="mb-3 text-sm text-emerald-400">
                        {byLine}
                      </div>
                      <button
                        type="button"
                        className="mt-auto rounded-lg bg-mirror-gold px-4 py-2.5 text-sm font-semibold text-mirror-bg hover:bg-mirror-gold-hover transition-colors disabled:opacity-60"
                        onClick={() => handleBuy(bundle.id)}
                        disabled={!!loadingId}
                      >
                        {loadingId === bundle.id ? 'Redirecting…' : 'Buy now'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-mirror-border bg-mirror-card p-8 text-center text-mirror-muted">
                No packs available right now.
              </div>
            )}
          </section>

          <section className="mt-10">
            <h2 className="mb-3 text-lg font-medium text-mirror-text">Transaction history</h2>
            {transactionsLoading && (
              <p className="rounded-xl border border-mirror-border bg-mirror-card py-4 text-center text-sm text-mirror-muted">
                Loading…
              </p>
            )}
            {transactionsError && (
              <p className="rounded-xl border border-mirror-border bg-mirror-card py-3 px-4 text-sm text-amber-300 border-l-4 border-l-amber-500/80">
                {transactionsError}
              </p>
            )}
            {!transactionsLoading && !transactionsError && (
              <>
                {transactions.length === 0 ? (
                  <p className="rounded-xl border border-mirror-border bg-mirror-card py-6 text-center text-sm text-mirror-muted">
                    No transactions yet.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-mirror-border">
                    <table className="min-w-full divide-y divide-mirror-border text-left text-sm">
                      <thead className="bg-mirror-card">
                        <tr>
                          <th className="py-2.5 pl-4 pr-2 font-medium text-mirror-muted">Date</th>
                          <th className="py-2.5 px-2 font-medium text-mirror-muted">Description</th>
                          <th className="py-2.5 px-2 font-medium text-mirror-muted text-right">Change</th>
                          <th className="py-2.5 pl-2 pr-4 font-medium text-mirror-muted text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-mirror-border bg-mirror-card">
                        {transactions.map((tx) => (
                          <TransactionRow key={tx.id} tx={tx} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function TransactionRow({ tx }: { tx: TokenTransaction }) {
  const date = typeof tx.createdAt === 'number'
    ? new Date(tx.createdAt)
    : new Date(tx.createdAt);
  const dateStr = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const label =
    tx.type === 'usage'
      ? 'Virtual try-on'
      : tx.type === 'topup_otp'
        ? 'One-time purchase'
        : tx.type === 'topup_subscription'
          ? 'Subscription'
          : 'Share to community';
  const change = tx.amount > 0 ? `+${tx.amount}` : String(tx.amount);
  const isCredit = tx.amount > 0;
  return (
    <tr>
      <td className="whitespace-nowrap py-2.5 pl-4 pr-2 text-mirror-muted">{dateStr}</td>
      <td className="py-2.5 px-2 text-mirror-text">{label}</td>
      <td className={`py-2.5 px-2 text-right font-medium ${isCredit ? 'text-emerald-400' : 'text-mirror-muted'}`}>
        {change}
      </td>
      <td className="whitespace-nowrap py-2.5 pl-2 pr-4 text-right text-mirror-muted">
        {tx.balanceAfter.toLocaleString()}
      </td>
    </tr>
  );
}
